import clsx from 'clsx'
import Badge from 'LuxKit/Badge'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { memo } from 'react'
import { hasAccountNotification } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { LargeButtonTypography } from 'LuxKit/Typography/ButtonTypography'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'

const IconContainer = styled.div`
  display: grid;
  grid-template: 1fr / 1fr;
  place-items: center;
`

const LuxPlusRing = styled.div`
  grid-column: 1;
  grid-row: 1;
  height: ${rem(32)};
  width: ${rem(32)};
  border-radius: ${props => props.theme.borderRadius.round};
  border: 2px solid ${props => props.theme.palette.product.luxPlus.background};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    height: ${rem(40)};
    width: ${rem(40)};
  }
`

const NotificationBadge = styled(Badge)`
  position: absolute;
  top: calc(30%);
  transform: translateY(-50%) translateX(100%);
  right: 0;
`

const Avatar = styled.div`
  position: relative;
  grid-column: 1;
  grid-row: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(24)};
  width: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  text-transform: uppercase;
  ${setSvgIconSize(16)}
  ${LargeButtonTypography}
  font-weight: ${props => props.theme.font.secondary.weight.bold};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  border: 2px solid transparent;

  ${mediaQueryUp.tablet} {
    ${setSvgIconSize(16)}
    height: ${rem(32)};
    width: ${rem(32)};
  }

  &.lux-plus-member {
    background-color: ${props => props.theme.palette.product.luxPlus.background};
  }

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    border-color: ${props => props.theme.palette.product.loyalty.bronze.normalForeground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    border-color: ${props => props.theme.palette.product.loyalty.silver.normalForeground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    border-color: ${props => props.theme.palette.product.loyalty.gold.normalForeground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    border-color: ${props => props.theme.palette.product.loyalty.platinum.normalForeground};
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
  &.tier-infinite {
    background: ${props => props.theme.palette.product.loyalty.infinite.gradientPrimaryBackground};
    border-color: ${props => props.theme.palette.product.loyalty.infinite.normalForeground};
    color: ${props => props.theme.palette.product.loyalty.infinite.normalContrast};
  }
`

function NavBarUserAvatar() {
  const account = useAppSelector(state => state.auth.account)
  const isLuxPlusMember = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isLoyaltyMember = useAppSelector(getHasLoyaltyAccount)
  const [tier] = useCurrentLoyaltyTier()
  const isLuxPlusAndLoyaltyMember = isLuxPlusMember && isLoyaltyMember
  const showNotification = useAppSelector(hasAccountNotification)

  const oneInitial = account.givenName?.[0]

  return <IconContainer>
    {isLuxPlusAndLoyaltyMember && <LuxPlusRing />}
    <Avatar className={clsx(
      { 'lux-plus-member': isLuxPlusMember },
      { [`tier-${tier}`]: isLoyaltyMember },
      { 'lux-plus-and-lux-loyalty-member': isLuxPlusAndLoyaltyMember },
    )}
    >
      {oneInitial}
      {!oneInitial && <LineUserIcon />}
      {showNotification && <NotificationBadge
        mode="critical"
        kind="dot"
      />}
    </Avatar>
  </IconContainer>
}

export default memo(NavBarUserAvatar)
